import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { applicationsAPI, servicesAPI } from '../services/api';
import { 
  Briefcase, 
  FileText, 
  Star, 
  User,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Card, CardHeader, CardBody, Badge, Button } from '../components/ui';

const IndividualDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    applications: 0,
    serviceRequests: 0,
    shortlisted: 0,
    pending: 0
  });
  const [recentApplications, setRecentApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appsRes, servicesRes] = await Promise.all([
        applicationsAPI.getMyApplications(),
        servicesAPI.getMyRequests()
      ]);
      
      const apps = appsRes.data.data || [];
      
      setStats({
        applications: appsRes.data.count || 0,
        serviceRequests: servicesRes.data.count || 0,
        shortlisted: apps.filter((a: any) => a.status === 'shortlisted').length,
        pending: apps.filter((a: any) => a.status === 'submitted' || a.status === 'reviewing').length
      });
      
      setRecentApplications(apps.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'submitted': return 'info';
      case 'reviewing': return 'warning';
      case 'shortlisted': return 'success';
      case 'interviewed': return 'primary';
      case 'offered': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'danger';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted': return <Clock className="w-4 h-4" />;
      case 'reviewing': return <AlertCircle className="w-4 h-4" />;
      case 'shortlisted': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const statCards = [
    {
      title: 'My Applications',
      value: stats.applications,
      icon: <FileText className="w-6 h-6" />,
      color: 'primary',
      subtitle: `${stats.pending} pending review`
    },
    {
      title: 'Shortlisted',
      value: stats.shortlisted,
      icon: <Star className="w-6 h-6" />,
      color: 'success',
      subtitle: 'Interview ready'
    },
    {
      title: 'Service Requests',
      value: stats.serviceRequests,
      icon: <Briefcase className="w-6 h-6" />,
      color: 'warning',
      subtitle: 'Active requests'
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white mb-6 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Welcome back, {user?.firstName}!</h1>
            <p className="text-primary-100 mt-1">Track your job applications and opportunities</p>
          </div>
          <Link to="/jobs">
            <Button variant="secondary" size="sm" icon={<Briefcase className="w-4 h-4" />}>
              Browse Jobs
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-500">{stat.title}</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">{stat.value}</p>
                <p className="text-sm text-secondary-500 mt-1">{stat.subtitle}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100 text-${stat.color}-600`}>
                {stat.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Link to="/jobs">
          <Card hover className="p-4 cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Browse Jobs</p>
                <p className="text-xs text-secondary-500">Find opportunities</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/opportunities">
          <Card hover className="p-4 cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-success-50 text-success-600 group-hover:bg-success-600 group-hover:text-white transition-colors">
                <Star className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Scholarships</p>
                <p className="text-xs text-secondary-500">Find funding</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/services">
          <Card hover className="p-4 cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-warning-50 text-warning-600 group-hover:bg-warning-600 group-hover:text-white transition-colors">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Services</p>
                <p className="text-xs text-secondary-500">Request service</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/my-applications">
          <Card hover className="p-4 cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-info-50 text-info-600 group-hover:bg-info-600 group-hover:text-white transition-colors">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">My Profile</p>
                <p className="text-xs text-secondary-500">Manage account</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Recent Applications */}
      <Card padding="none">
        <CardHeader 
          action={
            <Link to="/my-applications">
              <Button variant="ghost" size="sm" icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                View All
              </Button>
            </Link>
          }
        >
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Recent Applications</h3>
            <p className="text-sm text-secondary-500 mt-0.5">Your latest job applications</p>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {loading ? (
            <div className="p-8 text-center text-secondary-500">Loading...</div>
          ) : recentApplications.length > 0 ? (
            <div className="divide-y divide-secondary-100">
              {recentApplications.map((app) => (
                <div key={app._id} className="p-4 hover:bg-secondary-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900">{app.job?.title}</p>
                        <p className="text-sm text-secondary-500">{app.job?.business?.companyName || 'Company'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-secondary-500">
                        {new Date(app.createdAt).toLocaleDateString()}
                      </span>
                      <Badge variant={getStatusBadgeVariant(app.status)}>
                        {getStatusIcon(app.status)}
                        <span className="ml-1 capitalize">{app.status}</span>
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-secondary-500 mb-4">No applications yet</p>
              <Link to="/jobs">
                <Button variant="primary" size="sm">Browse Jobs</Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default IndividualDashboard;
